import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { publicApi, linePublicApi } from '../../services/api';
import { useSSE } from '../../hooks/useSSE';

interface QueueStatus {
  storeName: string;
  storeLogo: string | null;
  storeAddress: string | null;
  isActive: boolean;
  allowOnlineQueue: boolean;
  currentServing: number;
  waitingCount: number;
  estimatedWaitMinutes: number;
}

interface LineData {
  userId: string;
  displayName: string;
  pictureUrl: string | null;
}

const TakeNumberPage: React.FC = () => {
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<QueueStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    partySize: 1,
    customerName: '',
    note: '',
    customerLineId: '',
  });

  // LINE Login 狀態
  const [lineData, setLineData] = useState<LineData | null>(null);
  const [lineLoginUrl, setLineLoginUrl] = useState<string | null>(null);
  const [lineLoginEnabled, setLineLoginEnabled] = useState(false);

  // 解析 URL 中的 lineData（LINE OAuth 回調後）
  useEffect(() => {
    const lineDataParam = searchParams.get('lineData');
    if (lineDataParam) {
      try {
        const decoded = JSON.parse(atob(lineDataParam));
        setLineData(decoded);
        setFormData((prev) => ({
          ...prev,
          customerName: decoded.displayName || prev.customerName,
          customerLineId: decoded.userId,
        }));
      } catch (e) {
        console.error('Failed to parse lineData:', e);
      }
    }
  }, [searchParams]);

  // 取得 LINE Login URL
  useEffect(() => {
    if (!storeId) return;
    linePublicApi.getLoginUrl(storeId).then((res) => {
      if (res.data.enabled) {
        setLineLoginEnabled(true);
        setLineLoginUrl(res.data.loginUrl);
      }
    }).catch(() => {});
  }, [storeId]);

  const fetchStatus = useCallback(async () => {
    if (!storeId) return;
    try {
      const response = await publicApi.getStatus(storeId);
      setStatus(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || '找不到店家');
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  // 初次載入
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // SSE 即時更新
  useSSE({
    storeId: storeId || '',
    enabled: !!storeId,
    onEvent: () => { fetchStatus(); },
  });

  // 降級備援：30 秒輪詢
  useEffect(() => {
    if (!storeId) return;
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, [storeId, fetchStatus]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!storeId || submitting) return;

    setSubmitting(true);
    setError(null);

    try {
      const response = await publicApi.takeNumber(storeId, formData);
      const { accessToken } = response.data;
      navigate(`/ticket/${accessToken}`);
    } catch (err: any) {
      setError(err.response?.data?.message || '取號失敗，請稍後再試');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  if (error && !status) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-950 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-xl text-gray-600 dark:text-gray-400 mb-4">{error}</p>
        </div>
      </div>
    );
  }

  if (!status) return null;

  const canTakeNumber = status.isActive && status.allowOnlineQueue;

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950">
      {/* 店家資訊 */}
      <div className="bg-brand-600 text-white px-4 py-8 text-center">
        {status.storeLogo && (
          <img src={status.storeLogo} alt={status.storeName} className="w-16 h-16 mx-auto mb-3 rounded-full object-cover" />
        )}
        <h1 className="text-2xl font-bold mb-1">{status.storeName}</h1>
        {status.storeAddress && (
          <p className="text-brand-200 text-sm">{status.storeAddress}</p>
        )}
      </div>

      {/* 目前等候狀態 */}
      <div className="px-4 py-6">
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-3xl font-bold text-brand-600">#{status.currentServing || 0}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">目前叫號</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-orange-500">{status.waitingCount}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">等候組數</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-blue-500">~{status.estimatedWaitMinutes}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">預估等待(分)</p>
            </div>
          </div>
        </div>

        {/* 不開放提示 */}
        {!canTakeNumber && (
          <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4 mb-6 text-center">
            <p className="text-yellow-800 dark:text-yellow-200 font-medium">
              {!status.isActive ? '排隊目前已暫停' : '目前不開放線上取號'}
            </p>
          </div>
        )}

        {/* 取號表單 */}
        {canTakeNumber && (
          <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-800 rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">線上取號</h2>

            {error && (
              <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg p-3 mb-4">
                <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
              </div>
            )}

            {/* LINE Login 按鈕 */}
            {lineLoginEnabled && !lineData && lineLoginUrl && (
              <div className="mb-4">
                <a
                  href={lineLoginUrl}
                  className="w-full flex items-center justify-center gap-2 text-white font-bold py-3 rounded-lg text-base transition-all duration-200 hover:opacity-90"
                  style={{ backgroundColor: '#06C755' }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                    <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
                  </svg>
                  使用 LINE 帳號取號
                </a>
                <p className="text-xs text-gray-400 text-center mt-2">綁定 LINE 帳號可接收叫號通知</p>
              </div>
            )}

            {/* LINE 已綁定 */}
            {lineData && (
              <div className="mb-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-3 flex items-center gap-3">
                {lineData.pictureUrl && (
                  <img src={lineData.pictureUrl} alt="" className="w-10 h-10 rounded-full" />
                )}
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-800 dark:text-green-200">{lineData.displayName}</p>
                  <p className="text-xs text-green-600 dark:text-green-400">LINE 帳號已綁定，將接收叫號通知</p>
                </div>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="#06C755">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                </svg>
              </div>
            )}

            {/* 人數 */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                用餐人數 <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, partySize: Math.max(1, formData.partySize - 1) })}
                  className="w-10 h-10 rounded-full bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-300 font-bold text-lg"
                >
                  -
                </button>
                <span className="text-2xl font-bold text-gray-900 dark:text-white w-12 text-center">{formData.partySize}</span>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, partySize: Math.min(20, formData.partySize + 1) })}
                  className="w-10 h-10 rounded-full bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-300 font-bold text-lg"
                >
                  +
                </button>
              </div>
            </div>

            {/* 姓名（選填） */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">姓名（選填）</label>
              <input
                type="text"
                value={formData.customerName}
                onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                placeholder="方便叫號時辨識"
              />
            </div>

            {/* 備註（選填） */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">備註（選填）</label>
              <input
                type="text"
                value={formData.note}
                onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                placeholder="如：需要兒童座椅、輪椅"
              />
            </div>

            {/* 取號按鈕 */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-brand-600 hover:bg-brand-700 text-white font-bold py-4 rounded-lg text-lg transition-all duration-200 disabled:opacity-50"
            >
              {submitting ? '取號中...' : '取號'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default TakeNumberPage;
